import { useCallback, useEffect, useMemo, useState } from 'react';

import { envVariables } from '@/utils/env';

import type { AuthApi, AuthUser } from './types';

type UserInfoResponse = {
  username: string;
  firstName: string;
  roles: string[];
};

/**
 * Auth adapter voor de pure BFF (Backend-for-Frontend) authenticatiestrategie.
 *
 * De frontend verwerkt nooit tokens: login/logout worden afgehandeld door de backend.
 * Sessie-informatie wordt opgehaald via GET /auth/gebruikersinfo (leest het AUTH_TOKEN cookie).
 * Alle API-aanroepen sturen automatisch het AUTH_TOKEN cookie mee via credentials: 'include'.
 */
export default function useBffCookieAdapter(): AuthApi {
  const { apiUrlNS, authRedirectUri } = envVariables();
  const backendBase = apiUrlNS ?? '';

  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginError, setLoginError] = useState(false);

  useEffect(() => {
    const loginParam = new URLSearchParams(window.location.search).get('login');

    // Verwijder login query param direct zodat het niet in de URL blijft
    if (loginParam) {
      const params = new URLSearchParams(window.location.search);
      params.delete('login');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    }

    if (loginParam === 'error') {
      setLoginError(true);
      setIsLoading(false);
      return;
    }

    // Vraag de huidige gebruikersgegevens op (e-mail, gebruikersnaam, rollen, enz.) (verstuurt cookies via credentials:'include').
    // Als de gebruiker is geauthenticeerd, vul de gebruikersstatus met de geretourneerde gegevens; anders wis de gebruiker.
    // Stel loading altijd in op false wanneer het proces is voltooid.
    fetch(`${backendBase}/auth/gebruikersinfo`, { credentials: 'include' })
      .then((res) => (res.ok ? (res.json() as Promise<UserInfoResponse>) : null))
      .then((info) => {
        if (info) {
          setUser({
            username: info.username,
            firstName: info.firstName,
            allRolesForUser: info.roles ?? [],
          });
        } else {
          setUser(null);
        }
      })
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, [backendBase]);

  const login = useCallback(async () => {
    const params = new URLSearchParams({ 'return-url': window.location.origin });
    if (authRedirectUri) {
      params.set('redirect-uri', authRedirectUri);
    }
    const queryString = params.toString();
    window.location.href = `${backendBase}/auth/login${queryString ? `?${queryString}` : ''}`;
  }, [backendBase, authRedirectUri]);

  const logout = useCallback(async () => {
    // Logout is een POST (geen GET) zodat een uitlog-actie niet via een cross-site GET
    // (bijv. een <img>-tag) kan worden geforceerd. De backend wist de cookies en geeft de
    // Azure AD end-session URL terug, waarna we de browser daarheen navigeren.
    const returnUrl = encodeURIComponent(window.location.origin);
    try {
      const res = await fetch(`${backendBase}/auth/logout?return-url=${returnUrl}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const { logoutUrl } = (await res.json()) as { logoutUrl: string };
        window.location.href = logoutUrl;
        return;
      }
    } catch {
      // Val terug op een herlaad van de app hieronder.
    }
    // Fallback: als de logout-aanroep mislukt, herlaad de app zodat de sessiestatus opnieuw wordt bepaald.
    window.location.href = window.location.origin;
  }, [backendBase]);

  const getAccessToken = useCallback(async (): Promise<string | undefined> => {
    /**
     * Retourneert opzettelijk undefined voor de BFF-flow.
     *
     * De AuthApi-interface wordt gedeeld door meerdere authenticatiemechanismen:
     * - OIDC op Red Hat, waar de frontend mogelijk een toegangstoken moet verstrekken.
     * - MSAL op Azure met behulp van het BFF-patroon, waarbij tokens alleen door de backend worden verwerkt.
     *
     * In de BFF-flow leest of bewaart de frontend nooit toegangstokens.
     * Authenticatie is gebaseerd op HTTP-only cookies die automatisch worden verzonden met
     * API-verzoeken via credentials: 'include'.
     */
    return undefined;
  }, []);

  const onTokenExpired = useCallback<AuthApi['onTokenExpired']>(() => {
    /**
     * Opzettelijk een no-op voor de BFF-flow.
     *
     * Deze methode bestaat omdat de gedeelde AuthApi-interface ook de
     * OIDC-flow op Red Hat ondersteunt, waarbij de frontend mogelijk moet reageren op het verlopen van tokens.
     *
     * In de MSAL/BFF-flow worden het verlopen en vernieuwen van tokens door de backend afgehandeld,
     * bijvoorbeeld met behulp van de tokencache/cookies aan de backend-zijde. De frontend
     * beheert geen gebeurtenissen met betrekking tot de levensduur van tokens.
     *
     * We retourneren een lege unsubscribe-functie om te voldoen aan het AuthApi-contract.
     */
    return () => {};
  }, []);

  const onSilentRenewError = useCallback<AuthApi['onSilentRenewError']>(() => {
    /**
     * Registreert een callback voor stille vernieuwingsfouten.
     *
     * Relevant voor frontend-beheerde OIDC-flows.
     * Voor BFF-gebaseerde MSAL-flows kan dit als een no-op worden geïmplementeerd, omdat stille vernieuwing niet in de frontend plaatsvindt.
     *
     * Retourneert een functie om het abonnement op te zeggen.
     */
    return () => {};
  }, []);

  return useMemo<AuthApi>(
    () => ({
      provider: 'bff-cookie',
      user,
      isAuthenticated: !!user,
      isLoading,
      loginError,
      getAccessToken,
      login,
      logout,
      onTokenExpired,
      onSilentRenewError,
    }),
    [user, isLoading, loginError, getAccessToken, login, logout, onTokenExpired, onSilentRenewError],
  );
}
